import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/hooks';
import type { Auth } from '@/features/auth/hooks';
import { Sparkles } from '@/components/sparkles';
import { useGetUserFlags, useSetUserFlags } from '@/features/user-flags/hooks';
import type { ZendeskChatAccess } from '@/features/zendesk-chat/api';
import { postZendeskChatToken } from '@/features/zendesk-chat/api';
import { useGetZendeskAccess } from '@/features/zendesk-chat/hooks';
import { useTrackEvent } from '@/features/track/hook';
import clsx from 'clsx';
import { IconButton } from '../icon-button';
import * as styles from './index.css';
import { sentryCaptureException } from '@/utils/sentry';

const DEFAULT_DELAY_MS = 1000;
const MAX_DELAY_MS = 8000;
const TOKEN_LIFETIME_REFRESH_THRESHOLD = 0.8;

type SupportChatWidgetProps = {
  access: ZendeskChatAccess | null | undefined;
  auth: Auth | null | undefined;
};

function SupportChatWidget({ access, auth }: SupportChatWidgetProps) {
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [showSparkle, setShowSparkle] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { data: flags } = useGetUserFlags();
  const { mutate: mutateUserFlags } = useSetUserFlags();
  const authTsRef = useRef(0);
  const delayRef = useRef(DEFAULT_DELAY_MS);
  const isAuthenticatingBeforeOpenRef = useRef(false);
  const track = useTrackEvent();

  const openChatWidget = () => {
    setUnreadChatCount(0);
    window.zE?.('messenger', 'open');
  };

  const triggerAuthentication = () => {
    setShowSpinner(true);
    window.zE?.('messenger', 'loginUser', authenticateWithZendesk);
  };

  const onChatClick = () => {
    if (showSpinner) {
      return;
    }

    // proactively refresh token if it's about to expire
    const tokenLifetime = (access?.token_lifetime_minutes ?? 90) * 60 * 1000;
    if (authTsRef.current + tokenLifetime * TOKEN_LIFETIME_REFRESH_THRESHOLD <= Date.now()) {
      isAuthenticatingBeforeOpenRef.current = true;
      triggerAuthentication();
      return;
    }

    openChatWidget();

    track('Support Chat Clicked', { 'Account Id': auth?.account?.id });
  };

  const authenticateWithZendesk = async (callback: (token: string) => void) => {
    try {
      if (authTsRef.current === 0 && access?.token != null) {
        callback(access.token);
      } else {
        await postZendeskChatToken(auth?.account?.id, auth?.user?.id).then((data) => {
          if ((data?.token?.length ?? 0) === 0) {
            // API returns empty string for non-production accounts
            return;
          }

          callback(data.token);
          delayRef.current = DEFAULT_DELAY_MS;
        });
      }
    } catch (err) {
      sentryCaptureException(err);
      delayRef.current += DEFAULT_DELAY_MS;
    }

    // HOTFIX: quick way to "wait" for Zendesk authentication to be complete
    setTimeout(() => {
      if (delayRef.current >= MAX_DELAY_MS) {
        delayRef.current = DEFAULT_DELAY_MS;
      }

      authTsRef.current = Date.now();
      setShowSpinner(false);

      if (!isInitialized) {
        // all widget-related functionality is hidden on script load, so show after init
        setIsInitialized(true);
        window.zE?.('messenger', 'show');
      }

      if (isAuthenticatingBeforeOpenRef.current) {
        isAuthenticatingBeforeOpenRef.current = false;
        openChatWidget();
      }
    }, delayRef.current);
  };

  const disableSparkle = () => {
    if (!showSparkle) {
      return;
    }

    mutateUserFlags({
      accountId: auth?.account?.id,
      userId: auth?.user?.id,
      flags: {
        show_chat_sparkles: false,
      },
    });
    setShowSparkle(false);
  };

  useEffect(() => {
    if (flags !== undefined) {
      setShowSparkle(flags.show_chat_sparkles);
    }
  }, [flags]);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    const conversationTags = access?.is_handover_allowed === true ? [] : ['maa-ai-only'];
    window.zE?.('messenger:set', 'conversationTags', conversationTags);
    window.zE?.('messenger:on', 'unreadMessages', setUnreadChatCount);

    triggerAuthentication();
  }, []);

  if (!isInitialized) {
    // prevent widget from being accessible until all hooks are attempted
    return null;
  }

  return (
    <Sparkles onHide={disableSparkle} show={showSparkle}>
      <IconButton
        icon="chat"
        label="Support Chat"
        isLoading={showSpinner}
        onClick={onChatClick}
        data-test-support-chat
        className={clsx(styles.chatButton, unreadChatCount > 0 && styles.chatControlUnread)}
      >
        <span className={clsx(styles.chatCount, unreadChatCount > 0 && styles.chatCountUnread)}>{unreadChatCount}</span>
      </IconButton>
    </Sparkles>
  );
}

function SupportChat() {
  const auth = useAuth();
  const accessData = useGetZendeskAccess();

  const isProduction = __CIO_ENV__ === 'production';
  const hasWidget = Boolean(window.zE?.widget);

  const shouldChatShow = !accessData.isLoading && isProduction && hasWidget;

  return shouldChatShow ? <SupportChatWidget access={accessData.data} auth={auth.data} /> : null;
}

export { SupportChat };
